<script>
	import { onMount } from "svelte";

	import { Router, Link, Route, navigate } from "svelte-routing";
	import TopAppBar, { Row, Section, Title, AutoAdjust } from "@smui/top-app-bar";
	import Button, { Label } from "@smui/button";
	import Card, { Content, Actions } from "@smui/card";
	import IconButton from "@smui/icon-button";
	import List, { Item, Text, Graphic, Separator, Subheader } from "@smui/list";

	import NavLink from "./components/NavLink.svelte";
	import Login from "./pages/Login.svelte";
	import Projects from "./pages/Projects.svelte";
	import Graphics from "./pages/graphics/Graphics.svelte";
	import OrganizationsSelection from "./components/OrganizationsSelection.svelte";
	import LanguagesSelection from "./components/LanguagesSelection.svelte";
	import ProjectsSelection from "./components/ProjectsSelection.svelte";

	import api from "./api";
	import { loadScript, isTrustedScriptUrl, transformStringToInputObject } from "./utils";
	import {
		routes,
		routesById,
		embedInfo,
		loggedin,
		user,
		initialUserSettings,
		projects,
		graphics,
		fontthemes,
		languages,
		colorthemes,
		colorthemeDefinitions,
		organizations,
		organizationsById,
		selectedOrganization,
		selectedOrganizationObj,
		selectedLanguage,
		selectedProject,
		selectedProjectObj,
		setupComplete,
		organizationProjectSettings,
		fetchOrganizations,
		fetchOrganizationProjectSettings,
		fetchProjectColorThemes,
		fetchProjects,
		debug,
	} from "./stores";

	window.api = api;

	$: $debug = isDev || $selectedOrganization === "dummy_organisation";

	let topAppBar;

	// ///////////////////////////////////////////////////////////
	// define routes
	// ///////////////////////////////////////////////////////////
	const defRoutes = [
		{ id: "login_home", name: "", path: "/", component: Login, debug: false },
		{ id: "login", name: "Login", path: "login", component: Login, debug: false },
		{ id: "graphics", name: "Graphics", path: "graphics", component: Graphics, debug: false },
		{ id: "projects", name: "Projects", path: "projects", component: Projects, debug: false },
	];

	$routes = defRoutes;

	// ///////////////////////////////////////////////////////////
	// login/logout
	// ///////////////////////////////////////////////////////////
	const storeUser = () => {
		$user = api.user;
	};

	api.loginCallback = storeUser;
	api.logoutCallback = storeUser;

	const logout = async () => {
		try {
			await api.logout();
			$selectedOrganization = undefined;
		} catch (err) {
			console.log(err);
		}
	};

	onMount(async () => {
		//await api.login("admin", "password");
		await api.init();
		storeUser();
	});

	// ///////////////////////////////////////////////////////////
	// get data from backend
	// ///////////////////////////////////////////////////////////
	const getData = async () => {
		$languages = (await api.get("languages/")).map((l) => ({ ...l, id: l.code }));
		$initialUserSettings = await api.get(`userSettings/${$user.settings.id}/`);

		await fetchOrganizations();

		const organizationsIncludeCurrent = $initialUserSettings.organisations.includes(
			$initialUserSettings.current_organisation
		);
		$selectedOrganization = organizationsIncludeCurrent
			? $initialUserSettings.current_organisation
			: $initialUserSettings.organisations[0];

		const languagesIncludeCurrent = $selectedOrganizationObj.languages.includes($initialUserSettings.current_language);
		$selectedLanguage = languagesIncludeCurrent
			? $initialUserSettings.current_language
			: $organizationsById[$selectedOrganization].languages[0];

		fetchProjects();
		$fontthemes = await api.get("fontthemes/");

		if ($debug) console.log("USER SETTINGS", $initialUserSettings);
		if ($debug) console.log("PROJECTS", $projects);
		if ($debug) console.log("ORGANIZATIONS", $organizations);
	};

	// ///////////////////////////////////////////////////////////
	// get embed info aka resize script from backend
	// ///////////////////////////////////////////////////////////
	const getEmbedInfo = async () => {
		$embedInfo = await api.get("embed_info/");
		if (isTrustedScriptUrl($embedInfo.resize_script)) loadScript($embedInfo.resize_script);
	};

	// ///////////////////////////////////////////////////////////
	// create reactive variable that shows if all necessary
	// initial data was received from the backend
	// ///////////////////////////////////////////////////////////
	$: if ($loggedin)
		Promise.all([getEmbedInfo(), getData()]).then(() => {
			$setupComplete = true;
		});
	$: if (!$loggedin) $setupComplete = false;

	// $: if ($debug) console.log ("SETUP COMPLETE", $setupComplete);
	// $: if ($debug) console.log ("LOGGED IN ", $loggedin, $user);
	// $: if ($debug) console.log ("SELECTED org and language", $selectedOrganization, $selectedLanguage);
	// $: if ($debug) console.log ("SELECTED project", $selectedProject, $selectedProjectObj);
	// $: if ($debug) console.log ("ORGANIZATION PROJECT SETTINGS", $organizationProjectSettings);

	// ///////////////////////////////////////////////////////////
	// send data to backend
	// ///////////////////////////////////////////////////////////
	// if user changes language or organization, send to backend
	const saveUserSetting = (setting) =>
		api.patch(`userSettings/${$user.settings.id}/`, setting).catch((err) => console.error("failed to save user setting", err));
	$: if ($setupComplete) saveUserSetting({ current_language: $selectedLanguage });
	$: if ($setupComplete) saveUserSetting({ current_organisation: $selectedOrganization });

	// ///////////////////////////////////////////////////////////
	// react to user input
	// ///////////////////////////////////////////////////////////
	// // if the newly selected organization does not support the selected language, reset the language
	$: if ($selectedOrganizationObj && !$selectedOrganizationObj.languages.includes($selectedLanguage))
		$selectedLanguage = $organizationsById[$selectedOrganization].languages[0];

	// if project or organization is changed, get the corresponding settings
	$: $selectedProject, $selectedOrganization, fetchOrganizationProjectSettings();

	// if organization changes, get the projects that belong to the organization
	$: $selectedOrganization, fetchProjects();

	// if project changes, get the project color themes
	$: $selectedProject, fetchProjectColorThemes();

	// function getProps({ location, href, isPartiallyCurrent, isCurrent }) {
	// 	console.log(location, href, isCurrent, isPartiallyCurrent);
	// 	const isActive = href === "/" ? isCurrent : isPartiallyCurrent || isCurrent;

	// 	// The object returned here is spread on the anchor element's attributes
	// 	if (isActive) {
	// 		return { class: "active" };
	// 	}
	// 	return {};
	// }
</script>

<Router>
	<TopAppBar class="top-app-bar" bind:this={topAppBar} variant="fixed">
		<Row>
			<Section class="logo-section">
				<Title>
					<Button on:click={navigate($routesById.projects.path)}>
						<div class="logo" />
					</Button>
				</Title>
			</Section>
			<Section class="project-section">
				<ProjectsSelection />
				<nav>
					{#each $routes as r}
						{#if r.debug}
							<Link to={r.path}>{r.name}</Link>
						{/if}
					{/each}
				</nav>
			</Section>
			{#if $loggedin}
				<Section align="end" toolbar class="organization-language-section">
					<OrganizationsSelection />
					<LanguagesSelection />
				</Section>
				<Section align="end" toolbar class="user-section">
					<div class="user">{$user.username}</div>
					<IconButton on:click={() => logout()} class="material-icons">logout</IconButton>
				</Section>
			{/if}
		</Row>
	</TopAppBar>
	<AutoAdjust class="site" {topAppBar}>
		{#each $routes as r}
			<Route path={r.path} component={r.component} />
		{/each}
	</AutoAdjust>
</Router>

<svelte:head>
	<title>Euroviz GMS</title>
	<html lang="en" />
	<link rel="stylesheet" href="fonts/material-icons.css" />
	<link rel="stylesheet" href="fonts/roboto.css" />
	<link rel="stylesheet" href="fonts/roboto-mono.css" />
	<link rel="stylesheet" href="mdc.typography.14.0.0.css" />
	<link rel="stylesheet" href="build/smui.css" />
</svelte:head>

<style lang="scss">
	:global(body) {
		height: 100vh;
		margin: 0;
	}

	$bar-height: 70px;

	@media (max-width: 599px) {
		$bar-height: 56px;
	}

	:global(.mdc-top-app-bar__row) {
		height: $bar-height !important;
	}

	:global(main.site) {
		padding-top: $bar-height !important;
	}

	:global(main) {
		height: calc(100% - #{$bar-height} - 2px);
	}

	:global(a.nav) {
		text-decoration: none !important;
	}

	.logo {
		background-image: url(/assets/logo_euroviz.svg);
		background-size: contain;
		background-repeat: no-repeat;
		height: 37px;
		width: 205px;
		background-position: center left;
	}

	:global(.top-app-bar) {
		:global(.logo-section),
		:global(.user-section) {
			flex: 0 1 auto;
		}

		:global(.mdc-top-app-bar__title) {
			padding-left: 0px;
		}

		:global(.organization-language-section) {
			gap: 18px;
		}
	}

	.user {
		padding: 12px;
	}
</style>
